import { TypographyProps, theme } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-button
import { ButtonProps } from '@aloft/toolkit-sui/chakra';

import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';

export const fieldTypographyProps: TypographyProps = {
  variant: 'body1',
  color: 'textPrimary',
  __dangerDoNotUseSx: {
    width: '100%',
    height: PROPERTY_GRID_ROW_HEIGHT,
    backgroundColor: theme.colors.textOffset,
    padding: '4px 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textWrap: 'nowrap',
    whiteSpace: 'nowrap',
  },
};

export const readOnlyFieldTypographyProps: TypographyProps = {
  ...fieldTypographyProps,
  __dangerDoNotUseSx: {
    ...fieldTypographyProps.__dangerDoNotUseSx,
    backgroundColor: theme.colors.surface2,
  },
};

export const popoverInputFocusSx = {
  outline: '1.5px solid',
  outlineOffset: '-1.5px',
  outlineColor: theme.colors.selected2,
} as const;

export const ALERT_TARGET_FIELD_TITLE =
  'This field is triggering an alert. Please review and address the issue.';

export const alertTargetFieldHoverSx = {
  filter: 'brightness(0.96)',
  boxShadow: '0 2px 8px rgb(0 0 0 / 12%)',
} as const;

export const getAlertTargetInputFieldSx = (
  isAlertStyled: boolean,
  variant: 'error' | 'warning' = 'error',
  isAlertFocusStyled = false,
) => {
  if (!isAlertStyled) {
    return undefined;
  }

  const backgroundColor =
    variant === 'error' ? theme.colors.alertErrorLight : theme.colors.alertWarningLight;
  const borderColor = variant === 'error' ? theme.colors.alertError : theme.colors.alertWarning;

  return {
    backgroundColor,
    border: `2px solid ${borderColor}`,
    ...(isAlertFocusStyled ? alertTargetFieldHoverSx : {}),
    _hover: {
      backgroundColor,
      ...alertTargetFieldHoverSx,
    },
  };
};

export const getEditableFieldTriggerAlertProps = (
  isAlertStyled: boolean,
  variant: 'error' | 'warning' = 'error',
  isAlertFocusStyled = false,
) => {
  if (!isAlertStyled) {
    return undefined;
  }

  const backgroundColor =
    variant === 'error' ? theme.colors.alertErrorLight : theme.colors.alertWarningLight;
  const borderColor = variant === 'error' ? theme.colors.alertError : theme.colors.alertWarning;

  return {
    title: ALERT_TARGET_FIELD_TITLE,
    backgroundColor,
    border: `1px solid ${borderColor}`,
    ...(isAlertFocusStyled ? alertTargetFieldHoverSx : {}),
    _hover: {
      backgroundColor,
      ...alertTargetFieldHoverSx,
    },
  };
};

export const sharedButtonStyle: Partial<ButtonProps> = {
  width: '100%',
  height: PROPERTY_GRID_ROW_HEIGHT,
  borderRadius: '0',
  color: theme.colors.textPrimary,
  padding: '4px 8px',
  fontWeight: 'normal',
  outline: '0',
  _active: {},
  _focus: {},
  _focusVisible: {
    outline: '1.5px solid',
    outlineOffset: '-1.5px',
    outlineColor: '#3355db',
  },
  _focusWithin: {},
  _hover: { backgroundColor: theme.colors.surface2 },
};

export const popoverInputSx = {
  height: '100%',
  padding: '0',
  border: 'none',
  outline: 'none',
  borderRadius: '0',
  fontSize: '14px',
  _focusVisible: {},
} as const;

export const readOnlyFieldProps = (colSpan: number): TypographyProps => ({
  ...readOnlyFieldTypographyProps,
  __dangerDoNotUseSx: {
    ...readOnlyFieldTypographyProps.__dangerDoNotUseSx,
    gridColumn: `span ${colSpan}`,
  },
});

/**
 * Returns title + backgroundColor for a field that may be empty.
 * When `isEmpty` is true the cell is highlighted to prompt the user to fill it in.
 */
export const emptyFieldStyle = (
  isEmpty: boolean,
  emptyTitle = 'This field is empty. Please input a value.',
): { title?: string; backgroundColor: string } => ({
  title: isEmpty ? emptyTitle : undefined,
  backgroundColor: isEmpty ? theme.colors.selected4 : theme.colors.surface1,
});
